import CardWrapper from '../wrapper/card';
import { fetchReviewsThunk } from '../../redux/thunks/review';
import { formatDateWithTime } from '../../helper';
import GetReviewModal from './modal/get_reviews';
import ImageClass from '../../assets';
import ImportedReviewsModal from './modal/imported';
import { useParams } from 'react-router';
import { Add, KeyboardArrowLeftRounded } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../redux/store';
import { Avatar, Box, Button, CircularProgress, Pagination, Rating, Typography } from '@mui/material';
import { BusinessObject, ReviewObject } from '../../params/redux/review';
import { settingSearchedBusiness, settingSelectedBusiness } from '../../redux/slices/review';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

const REVIEWS_PER_PAGE = 15;

const ReviewComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const [gettingReviews, setGettingReviews] = useState<boolean>(false);
    const [reviewsImported, setReviewsImported] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [paginatedReviews, setPaginatedReviews] = useState<ReviewObject[]>([]);

    const { businesses, selectedBusiness, loaders } = useSelector((state: RootState) => state.review);

    const handleSelectBusiness = async (business: BusinessObject) => {
        setIsLoading(true);
        setPage(1);
        await dispatch(settingSelectedBusiness(business));
        setIsLoading(false);
    };

    useEffect(() => {
        if (selectedBusiness) {
            setIsLoading(true);
            setTimeout(() => {
                const startIndex = (page - 1) * REVIEWS_PER_PAGE;
                const endIndex = startIndex + REVIEWS_PER_PAGE;
                setPaginatedReviews(selectedBusiness.reviews.slice(startIndex, endIndex));
                setIsLoading(false);
            }, 500);
        }
    }, [selectedBusiness, page]);

    useEffect(() => {
        const fetchAllReviews = async () => {
            try {
                await dispatch(fetchReviewsThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
            } catch (e) {
                // Catch errors
            }
        };
        fetchAllReviews();
    }, []);

    const handleGetReviewsModal = () => {
        dispatch(settingSearchedBusiness([]));
        setGettingReviews(true);
    };

    if (loaders.isLoading || isLoading) {
        return <CardWrapper minHeight="200px">
            <Box display="flex" alignItems="center" flexDirection="column" height="200px" justifyContent="center">
                <CircularProgress />
                <Typography variant="body2">Loading...</Typography>
            </Box>
        </CardWrapper>;
    } else if (businesses.length === 0) {
        return <Box minHeight="70vh">
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={2}>
                <img src={ImageClass.noForm} alt="no overview" height="300px" />
                <Typography variant="subtitle1" color="textSecondary" align="center">Import reviews to gain meaningful insights</Typography>
                <Button variant="contained" onClick={handleGetReviewsModal} startIcon={<Add />}>Import Reviews</Button>
            </Box>
            <GetReviewModal open={gettingReviews} onClose={() => setGettingReviews(false)} openImportingModal={() => setReviewsImported(true)} />
            <ImportedReviewsModal open={reviewsImported} onClose={() => setReviewsImported(false)} />
        </Box>;
    }

    return <Box sx={{ mt: 4 }}>
        {selectedBusiness
            ? (
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Button startIcon={<KeyboardArrowLeftRounded />} onClick={() => dispatch(settingSelectedBusiness(null))} variant="outlined" sx={{ mb: 2 }}>
                            Back to Businesses
                        </Button>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar src={`https://maps.googleapis.com/maps/api/place/photo?maxwidth=500&photo_reference=${selectedBusiness.photo_reference}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`} alt={selectedBusiness.business_name} />
                            <Typography variant="h4" gutterBottom textAlign="center" fontWeight="bold">
                                {selectedBusiness.business_name}
                            </Typography>
                        </Box>
                        <Box></Box>
                    </Box>
                    {selectedBusiness.reviews.length === 0
                        ? <Box minHeight="60vh">
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={2}>
                                <img src={ImageClass.noForm} alt="no overview" height="300px" />
                                <Typography variant="subtitle1" color="textSecondary" align="center">No Reviews</Typography>
                            </Box>
                        </Box>
                        : <CardWrapper>
                            <Box display="flex" flexDirection="column" gap={2}>
                                {paginatedReviews.map((review) => (
                                    <CardWrapper key={review.id}>
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <Avatar src={review.profile_photo} alt={review.profile_name} />
                                            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                                <Box>
                                                    <Typography variant="h6" fontWeight="bold">
                                                        {review.profile_name}
                                                    </Typography>
                                                    <Rating value={parseFloat(review.rating)} precision={0.1} readOnly />
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle1" color="textDisabled">{formatDateWithTime(review.created_at)}</Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Typography variant="body2" color="textSecondary" mt={2}>
                                            {review.review_text}
                                        </Typography>
                                    </CardWrapper>
                                ))}
                            </Box>

                            {/* Pagination Component */}
                            <Box display="flex" justifyContent="center" mt={2}>
                                <Pagination
                                    count={Math.ceil(selectedBusiness.reviews.length / REVIEWS_PER_PAGE)}
                                    page={page}
                                    onChange={(_, value) => setPage(value)}
                                    color="primary"
                                />
                            </Box>
                        </CardWrapper>}
                </Box>
            )
            : (
                // Show list of all businesses
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box></Box>
                        <Typography variant="h4" gutterBottom textAlign="center" fontWeight="bold">
                            Business Reviews
                        </Typography>
                        <Button variant="contained" onClick={handleGetReviewsModal} startIcon={<Add />}>Import Reviews</Button>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {businesses.map((business: BusinessObject) => (
                            <Box key={business.id}>
                                <CardWrapper>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <Avatar src={`https://maps.googleapis.com/maps/api/place/photo?maxwidth=500&photo_reference=${business.photo_reference}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`} alt={business.business_name} />
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                onClick={() => handleSelectBusiness(business)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {business.business_name}
                                            </Typography></Box>
                                        <Box display="flex" flexDirection="column" alignItems="end">
                                            <Typography variant="body1" color="textDisabled">
                                                <b>Reviews Count:</b> {business?.reviews?.length || 0}
                                            </Typography>
                                            <Typography variant="body1" color="textDisabled">
                                                <b>Request at:</b> {formatDateWithTime(business.created_at)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardWrapper>
                            </Box>
                        ))}
                    </Box>
                </>
            )}
        <GetReviewModal open={gettingReviews} onClose={() => setGettingReviews(false)} openImportingModal={() => setReviewsImported(true)} />
        <ImportedReviewsModal open={reviewsImported} onClose={() => setReviewsImported(false)} />
    </Box>;
};

export default ReviewComponent;